// src/components/Inventory/TransactionsTable.js

import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleExclamation, faPenToSquare, faSpinner, faTruck } from "@fortawesome/free-solid-svg-icons";
import { makeInventorySx } from "./uiStyles";
import { useTranslation } from "react-i18next";
import { getTimeAgo } from "../../utils/dateUtils";

function TransactionsTable({ requests = [], listings = [], dateFrom, dateTo, materials = [], onLogPartial }) {
  const theme = useTheme();
  const sx = makeInventorySx(theme);
  const { t } = useTranslation();

  // briefly show a spinner on the clicked row's review button so the click feels
  // acknowledged before the modal mounts. timings follow existing conventions:
  // - 50ms mount delay matches the setIsVisible(true) pattern across receiver pages
  //   (lets the spinner paint before heavier modal work hits the main thread)
  // - 600ms spinner clear matches the post-animation flag-clear pattern in
  //   RoleSwitcher / LastUpdatedBadge — lands just after the dialogSlideIn 0.3s
  const [pendingId, setPendingId] = useState(null);

  const handleReviewClick = (row) => {
    setPendingId(row.id);
    setTimeout(() => onLogPartial(row), 50);
    setTimeout(() => setPendingId((cur) => (cur === row.id ? null : cur)), 600);
  };

  const statusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "delivered":
        return "success";
      case "scheduled":
        return "info";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const statusLabel = (statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();

    if (s.includes("deliver")) return t("schedule.statusChips.delivered", "RECEIVED");
    if (s.includes("schedule")) return t("schedule.statusChips.scheduled", "SCHEDULED");
    if (s.includes("pending") || s.includes("new")) return t("schedule.statusChips.pending", "PENDING");
    if (s.includes("late") || s.includes("overdue")) return t("schedule.statusChips.late", "LATE");
    if (s.includes("cancel")) return t("schedule.statusChips.cancelled", "CANCELLED");

    return t("schedule.statusChips.unknown", "UNKNOWN");
  };

  const data = useMemo(() => {
    const listingsById = new Map((listings || []).map((l) => [String(l.id), l]));

    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return (requests || [])
      .filter((r) => Boolean(r?.date_delivered))
      .filter((r) => {
        const d = new Date(r.date_delivered);
        if (Number.isNaN(d.getTime())) return false;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .map((r) => {
        const listing = listingsById.get(String(r.listing_id)) || {};
        const donor = listing.donor || listing.company_name || r.donor || "—";
        const material = listing.material || r.material || "—";

        const weight =
          Number(r?.weight ?? r?.quantity ?? listing?.weight ?? listing?.quantity) || 0;

        const status = (r?.status || "delivered").toLowerCase();

        return {
          id: r.id ?? `${r.listing_id}-${r.date_delivered}`,
          rawRequest: r,
          date: String(r.date_delivered).slice(0, 10),
          donor,
          material,
          weight,
          status,
          quantityAccepted: r.quantity_accepted,
          rejectionReason: r.rejection_reason,
          rejectionNotes: r.rejection_notes,
          firstLoggedAt: r.first_logged_at,
          lastModifiedBy: r.last_modified_by,
          dateModified: r.date_modified,
        };
      })
      .filter((row) => {
        if (!materials || materials.length === 0) return true;
        return materials.includes(row.material);
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [requests, listings, dateFrom, dateTo, materials, t]);

  // delivered pickups with no acceptance logged yet are awaiting review.
  // count surfaces under the table subtitle; row className tints the row.
  const needsReview = (row) => row.status === "delivered" && row.quantityAccepted == null;
  const reviewCount = useMemo(() => data.filter(needsReview).length, [data]);

  const recordsLabel = t("Inventory.Records", {
    count: data.length,
    defaultValue: "{{count}} record",
  });

  const recordsLabelFallback =
    data.length === 1
      ? `${data.length} ${t("record")}`
      : `${data.length} ${t("records")}`;

  return (
    <Paper elevation={0} sx={sx.cardSx}>
      <Box sx={sx.cardHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {t("Pickups History")}
          </Typography>
          <Typography variant="caption" sx={sx.captionSx}>
            {t("Confirmed receptions (delivered)")}
          </Typography>
          {onLogPartial && reviewCount > 0 && (
            <Typography
              variant="caption"
              className="tx-review-count"
              sx={{ display: "block", mt: 0.5 }}
            >
              <FontAwesomeIcon icon={faCircleExclamation} style={{ marginRight: 6 }} />
              {t("{{count}} of {{total}} pickups awaiting your review", { count: reviewCount, total: data.length })}
            </Typography>
          )}
        </Box>

        <Chip
          size="small"
          variant="outlined"
          label={recordsLabel !== "Inventory.Records" ? recordsLabel : recordsLabelFallback}
          sx={sx.chipSx}
        />
      </Box>

      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small" sx={sx.tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{t("Date")}</TableCell>
              <TableCell>{t("Donor")}</TableCell>
              <TableCell>{t("Material")}</TableCell>
              <TableCell align="right">{t("Weight (lbs)")}</TableCell>
              <TableCell>{t("Status")}</TableCell>
              {onLogPartial && <TableCell align="center">{t("Review")}</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody sx={sx.zebraBodySx}>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={onLogPartial ? 6 : 5} sx={{ py: 3, opacity: 0.75 }}>
                  {t("No pickups found for this filter.")}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const ago = getTimeAgo(row.date);
                const isNeedsReview = onLogPartial && needsReview(row);
                return (
                  <TableRow
                    key={row.id}
                    className={isNeedsReview ? "tx-row--needs-review" : undefined}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FontAwesomeIcon icon={faTruck} style={{ fontSize: "0.75rem", color: "#669d2e", opacity: 0.7 }} />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600, fontSize: "0.8rem" }}>{ago || row.date}</span>
                          {ago && <span style={{ fontSize: "0.7rem", opacity: 0.6 }}>{row.date}</span>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.donor}</TableCell>
                    <TableCell>{row.material}</TableCell>
                    <TableCell align="right" sx={sx.numberCellSx}>
                      {Number(row.weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(row.status)}
                        color={statusColor(row.status)}
                        variant="filled"
                        sx={{ textTransform: "uppercase", fontWeight: 800 }}
                      />
                    </TableCell>
                    {onLogPartial && (
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {(() => {
                          const qa = row.quantityAccepted;
                          const isLogged = qa != null;
                          const isPartial = isLogged && qa < row.weight;
                          const isPending = pendingId === row.id;
                          const variant = isPartial ? "partial" : isLogged ? "full" : "none";
                          const icon = isPending
                            ? faSpinner
                            : variant === "partial"
                            ? faCircleExclamation
                            : variant === "full"
                            ? faCheck
                            : faPenToSquare;
                          return (
                            <button
                              type="button"
                              className={`rri-log-cell rri-log-cell--${variant}`}
                              aria-label={t("Review")}
                              onClick={() => handleReviewClick(row)}
                              disabled={isPending}
                            >
                              <FontAwesomeIcon icon={icon} spin={isPending} />
                            </button>
                          );
                        })()}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>

          {data.length > 0 && (
            <TableBody>
              <TableRow sx={{ "& td": { borderBottom: "none", fontWeight: 700 } }}>
                <TableCell>{t("Total")}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell align="right" sx={sx.numberCellSx}>
                  {data.reduce((s, r) => s + Number(r.weight || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell></TableCell>
                {onLogPartial && <TableCell></TableCell>}
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TransactionsTable;
